import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import SheetCard from '../components/SheetCard';
import { FileTextIcon, PlusIcon, SearchIcon, MoreVertical, Edit, Copy, Trash, Share, Download, ListPlus, FileInput, FileOutput, ArrowUpDown, ArrowUp, ArrowDown, Import, X, Wand2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import { useUIState } from "../contexts/UIStateContext";
import { getAllSheets, deleteSheet, getSheetById, saveSheet } from "../services/SheetStorageService";
import { exportSingleSheet } from "../services/ExportService";
import logger from "../services/LoggingService";
import { useSetlist } from "../contexts/SetlistContext";
import { useSheetActions } from "../contexts/ActionsContext";
import ConfirmModal from "../components/ConfirmModal";
import SetlistModal from "../components/SetlistModal";
import ImportModal from "../components/ImportModal";
import ExportModal from "../components/ExportModal";
import ForkOptionsModal from "../components/ForkOptionsModal";
import QuickDraftModal from "../components/QuickDraftModal";
import { fetchWithAuth, API_URL } from "../services/ApiService";
import { ForkAttribution, ForkCount } from "../components/ForkAttribution";
import ClickableUser from "../components/ClickableUser";
import SheetUsage from "../components/SheetUsage";
import CompactMediaPlayer from "../components/CompactMediaPlayer";

/**
 * Page displaying all available sheets categorized by ownership
 */
const SheetsPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const { isAuthenticated, showAuthModal, authChangeCounter, currentUser: user } = useAuth();
  const { handleContextMenu: uiHandleContextMenu } = useUIState();
  const { setSheetActions } = useSheetActions();
  const [isLoading, setIsLoading] = useState(true);
  const [allSheets, setAllSheets] = useState([]);
  const [activeFilters, setActiveFilters] = useState(new Set(['all'])); // Using Set for easy add/remove
  const [availableBands, setAvailableBands] = useState([]); // Store bands user is a member of

  // Sorting state
  const [sortBy, setSortBy] = useState('dateModified'); // title, dateCreated, dateModified, status, owner
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  
  // Context menu state
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [exportStatus, setExportStatus] = useState({ message: '', error: '' });
  const [setlistModalOpen, setSetlistModalOpen] = useState(false);
  const [selectedSheetForSetlist, setSelectedSheetForSetlist] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [forkModalOpen, setForkModalOpen] = useState(false);
  const [selectedSheetForFork, setSelectedSheetForFork] = useState(null);
  const [quickDraftModalOpen, setQuickDraftModalOpen] = useState(false);
  const [quickDraftEligible, setQuickDraftEligible] = useState(false);
  const inputRef = useRef();
  
  // Get setlist context
  const { addSheetToSetlist } = useSetlist();
  
  // State for confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    sheetId: null
  });
  
  // Fetch sheets from the API directly
  useEffect(() => {
    const fetchSheets = async () => {
      setIsLoading(true);
      try {
        // Get all sheets and bands from the API
        const [sheets, bandsResponse] = await Promise.all([
          getAllSheets(),
          fetch('/api/bands?category=my', { credentials: 'include' }).then(r => r.json())
        ]);

        logger.debug('SheetsPage', 'Fetched sheets from API:', sheets);
        logger.debug('SheetsPage', 'Fetched bands from API:', bandsResponse);

        // Process sheets to ensure they have the necessary properties
        const processedSheets = sheets.map(sheet => {
          // Extract song information from the sheet data
          // Check if we're dealing with legacy data format
          if (Array.isArray(sheet.sections) && sheet.sections.length > 0) {
            // Get BPM from various possible locations
            const songBpm = sheet.bpm || // Direct BPM
                          (sheet.song?.bpm) || // In song object
                          (sheet.songInfo?.bpm) || // In songInfo object
                          (sheet.sections[0]?.bpm) || // In the first section
                          null; // Default to null if not found

            return {
              ...sheet,
              // Ensure these properties are accessible for display
              bpm: songBpm,
              title: sheet.title || (sheet.song?.title) || 'Untitled',
              artist: sheet.artist || (sheet.song?.artist) || 'Unknown Artist',
              status: sheet.status || 'WIP'
            };
          }
          return {
            ...sheet,
            status: sheet.status || 'WIP'
          };
        });

        logger.debug('SheetsPage', 'Processed sheets:', processedSheets.slice(0, 2)); // Log first 2 sheets

        // Store all sheets and extract unique bands
        setAllSheets(processedSheets);

        // Extract bands from the response (handle both array and object with 'bands' property)
        const bands = Array.isArray(bandsResponse) ? bandsResponse : (bandsResponse?.bands || []);
        setAvailableBands(bands);

        logger.debug('SheetsPage', 'Available bands:', bands);
      } catch (error) {
        console.error('Error fetching sheets:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSheets();
  }, [authChangeCounter]); // Refetch when auth state changes

  // Check Quick Draft eligibility
  useEffect(() => {
    if (!isAuthenticated) {
      setQuickDraftEligible(false);
      return;
    }
    const checkEligibility = async () => {
      try {
        const response = await fetchWithAuth(`${API_URL}/quick-draft/eligibility`);
        if (response.success && response.data) {
          setQuickDraftEligible(response.data.eligible);
        }
      } catch {
        setQuickDraftEligible(false);
      }
    };
    checkEligibility();
  }, [isAuthenticated]);

  // Define the context menu items for different sheet types
  const getContextMenuItems = (sheetId, category) => {
    const menuItems = [
      {
        label: "Edit",
        action: () => navigate(`/sheet/${sheetId}`),
        icon: "edit"
      },
      {
        label: "Duplicate",
        action: () => alert(`Duplicating sheet ${sheetId} (mock function)`),
        icon: "copy"
      },
      {
        label: "Share",
        action: () => alert(`Sharing sheet ${sheetId} (mock function)`),
        icon: "share"
      }
    ];
    
    // Only show delete option for user's own sheets
    if (category === 'my') {
      menuItems.push({
        label: "Delete",
        action: () => handleSheetDelete(sheetId),
        icon: "trash",
        danger: true
      });
    }
    
    return menuItems;
  };

  const handleSheetDelete = async (sheetId) => {
    logger.debug('SheetsPage', 'handleSheetDelete called with ID:', sheetId);
    
    try {
      // Call the actual service function from SheetStorageService
      logger.debug('SheetsPage', 'Calling deleteSheet service function...');
      const success = await deleteSheet(sheetId);
      logger.debug('SheetsPage', 'deleteSheet result:', success);
      
      if (success) {
        logger.debug('SheetsPage', 'Sheet deleted successfully, updating UI state...');
        // Update local state by removing the deleted sheet
        setAllSheets(prevSheets => {
          logger.debug('SheetsPage', 'Current sheets count:', prevSheets.length);
          const filteredSheets = prevSheets.filter(sheet => {
            logger.debug('SheetsPage', 'Comparing sheet._id:', sheet._id, 'with sheetId:', sheetId);
            return sheet._id !== sheetId;
          });
          logger.debug('SheetsPage', 'New sheets count:', filteredSheets.length);
          return filteredSheets;
        });
        alert(`Sheet deleted successfully`);
      } else {
        console.error('deleteSheet returned false');
        alert('Failed to delete sheet. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleSheetDelete:', error);
      console.error('Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      alert('Error deleting sheet: ' + (error.message || 'Unknown error'));
    }
  };

  // Function to open context menu for a sheet
  const openSheetContextMenu = (e, sheetId, category) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Call our local handleOpenContextMenu instead of the UIStateContext's handleContextMenu
    handleOpenContextMenu(e, sheetId);
  };

  // Filter sheets based on search term
  const sortSheets = (sheets) => {
    return [...sheets].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'dateCreated':
          aValue = new Date(a.dateCreated || 0);
          bValue = new Date(b.dateCreated || 0);
          break;
        case 'dateModified':
          aValue = new Date(a.dateModified || 0);
          bValue = new Date(b.dateModified || 0);
          break;
        case 'owner':
          aValue = (a.owner?.username || a.owner || '').toLowerCase();
          bValue = (b.owner?.username || b.owner || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || 'WIP').toLowerCase();
          bValue = (b.status || 'WIP').toLowerCase();
          break;
        default:
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filter and sort sheets based on active filter tags
  const getFilteredAndSortedSheets = () => {
    let filtered = allSheets;

    // Apply tag filters (if not "all")
    if (!activeFilters.has('all')) {
      filtered = allSheets.filter(sheet => {
        // Check if any active filter matches this sheet
        for (const filter of activeFilters) {
          // "my sheets" filter - check if user owns the sheet
          if (filter === 'my_sheets' && user && sheet.owner === user._id) {
            return true;
          }
          // Band filter - check if sheet belongs to this band
          if (filter.startsWith('band_') && sheet.band_id === filter.substring(5)) {
            return true;
          }
        }
        return false;
      });
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(sheet =>
        sheet.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sheet.artist.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    return sortSheets(filtered);
  };

  // Handle adding/removing filter tags
  const toggleFilter = (filter) => {
    setActiveFilters(prev => {
      const newFilters = new Set(prev);

      // If clicking "all", clear all other filters
      if (filter === 'all') {
        return new Set(['all']);
      }

      // Remove "all" if adding a specific filter
      newFilters.delete('all');

      // Toggle the clicked filter
      if (newFilters.has(filter)) {
        newFilters.delete(filter);
      } else {
        newFilters.add(filter);
      }

      // If no filters remain, default to "all"
      if (newFilters.size === 0) {
        return new Set(['all']);
      }

      return newFilters;
    });
  };

  // Helper to handle renaming a sheet
  const handleRename = (sheet) => {
    setEditingId(sheet._id);
    setEditingValue(sheet.title || "");
    setMenuOpenId(null);
  };

  // Focus the input when editingId changes
  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  // Commit rename
  const commitRename = async (sheet) => {
    const newTitle = editingValue.trim();
    if (newTitle && newTitle !== sheet.title) {
      try {
        // Update the sheet in the database
        const updated = { ...sheet, title: newTitle };
        // Call API to update sheet
        // For now, just update local state
        setAllSheets(prevSheets =>
          prevSheets.map(s => s._id === sheet._id ? { ...s, title: newTitle } : s)
        );
      } catch (error) {
        console.error('Error updating sheet title:', error);
      }
    }
    setEditingId(null);
    setEditingValue("");
  };

  // Cancel rename
  const cancelRename = () => {
    setEditingId(null);
    setEditingValue("");
  };

  // Helper to show fork options modal
  const handleFork = (sheet) => {
    console.log('🔄 Fork button clicked for sheet:', sheet);
    setSelectedSheetForFork(sheet);
    setForkModalOpen(true);
    setMenuOpenId(null);
  };

  // Helper to actually create the fork
  const handleForkConfirm = async (forkOptions) => {
    if (!selectedSheetForFork) return;
    
    console.log('🔄 Creating fork with options:', forkOptions);
    try {
      // Get the full sheet data
      console.log('📥 Getting sheet data for ID:', selectedSheetForFork._id);
      const fullSheetData = await getSheetById(selectedSheetForFork._id);
      
      if (!fullSheetData) {
        console.error('❌ Could not load sheet data for forking');
        return;
      }
      
      console.log('✅ Sheet data loaded successfully:', fullSheetData);
      
      // Create a fork with "(Fork)" appended to the title
      const forkedSheet = {
        ...fullSheetData,
        title: `${fullSheetData.title} (Fork)`,
        // Add collaboration fields
        forked_from: selectedSheetForFork._id || selectedSheetForFork._id,
        is_private_fork: forkOptions.isPrivateFork,
        version_count: 1,
        latest_version_id: null,
        pending_merges: []
      };
      
      // Remove MongoDB _id to prevent duplicate key error
      // saveSheet handles application 'id' but not MongoDB '_id'
      delete forkedSheet._id;
      
      console.log('💾 Saving forked sheet:', forkedSheet);
      
      // Save as new sheet - saveSheet will handle application ID generation
      const savedSheet = await saveSheet(forkedSheet, true, 'FORK_OPERATION');
      
      console.log('✅ Sheet forked successfully, reloading page...');
      
      // Reload the page to show the forked sheet (same approach as delete)
      window.location.reload();
      
    } catch (error) {
      console.error('❌ Error forking sheet:', error);
      alert(`Error forking sheet: ${error.message}`);
    }
    
    setForkModalOpen(false);
    setSelectedSheetForFork(null);
  };
  
  // Helper to open setlist modal for adding a sheet to a setlist
  const handleAddToSetlist = (sheet) => {
    setSelectedSheetForSetlist(sheet);
    setSetlistModalOpen(true);
    setMenuOpenId(null);
  };
  
  // Helper to export a single sheet
  const handleExportSheet = async (sheet) => {
    try {
      setExportStatus({ message: '', error: '' });
      const result = await exportSingleSheet(sheet._id);
      setExportStatus({ message: result.message, error: '' });
      
      // Clear the success message after 3 seconds
      setTimeout(() => {
        setExportStatus({ message: '', error: '' });
      }, 3000);
    } catch (error) {
      setExportStatus({ message: '', error: error.message });
      
      // Clear the error message after 5 seconds
      setTimeout(() => {
        setExportStatus({ message: '', error: '' });
      }, 5000);
    }
    setMenuOpenId(null);
  };
  
  // Function for opening the delete confirmation dialog
  const confirmDeleteSheet = (id) => {
    logger.debug('SheetsPage', 'confirmDeleteSheet called with sheet ID:', id);
    try {
      // Find the sheet in the allSheets array
      const sheet = allSheets.find(s => s._id === id);
      logger.debug('SheetsPage', 'Found sheet:', sheet ? 'Yes' : 'No');

      if (!sheet) {
        logger.error('SheetsPage', 'Sheet not found in allSheets array');
        logger.debug('SheetsPage', 'Current allSheets:', allSheets);
        return;
      }

      const title = sheet.title || 'Untitled';
      logger.debug('SheetsPage', 'Preparing confirmation dialog for sheet:', title);
      
      // Show custom confirmation dialog
      logger.debug('SheetsPage', 'Setting confirm dialog state to open');
      setConfirmDialog({
        isOpen: true,
        title: "Delete Sheet",
        message: `Are you sure you want to delete "${title}"?`,
        onConfirm: async () => {
          logger.debug('SheetsPage', 'Confirmation dialog confirmed for sheet ID:', id);
          try {
            logger.debug('SheetsPage', 'Calling handleSheetDelete with ID:', id);
            await handleSheetDelete(id);
            logger.debug('SheetsPage', 'handleSheetDelete completed successfully');
            // Close the dialog
            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          } catch (error) {
            logger.error('SheetsPage', 'Error in confirmation dialog onConfirm function:', error);
            alert('Failed to delete sheet. Please try again.');
            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          }
        },
        sheetId: id
      });
      logger.debug('SheetsPage', 'Confirm dialog state set, dialog should be visible');
    } catch (error) {
      logger.error('SheetsPage', 'Error preparing delete confirmation:', error);
    }
  };
  
  // Handle opening context menu
  const handleOpenContextMenu = (e, sheetId) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Ensure the position is correct
    const menuWidth = 150; // Same as in the CSS for the menu
    let x = e.clientX;
    const y = e.clientY;
    
    // Adjust x if it would make the menu go off-screen
    const windowWidth = window.innerWidth;
    if (x + menuWidth > windowWidth) {
      x = windowWidth - menuWidth - 10; // 10px buffer
    }
    
    setMenuPos({ x, y });
    setMenuOpenId(sheetId);
  };
  
  // Handle click outside menu to close it
  useEffect(() => {
    if (!menuOpenId) return;
    
    const handleClickOutside = (e) => {
      // Don't close if clicking inside the menu
      if (e.target.closest('.context-menu-container')) {
        return;
      }
      setMenuOpenId(null);
    };
    
    // Add event listeners for mouse and touch events with a slight delay to avoid immediate closing
    setTimeout(() => {
      window.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("touchstart", handleClickOutside);
    }, 100);
    
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("touchstart", handleClickOutside);
    };
  }, [menuOpenId]);
  
  const handleCreateSheet = useCallback(() => {
    if (!isAuthenticated) {
      showAuthModal();
      return;
    }
    navigate('/sheet/new');
  }, [isAuthenticated, showAuthModal, navigate]);
  
  // Register the sheet actions with the context
  useEffect(() => {
    setSheetActions({
      handleCreateSheet
    });
  }, [setSheetActions, handleCreateSheet]);

  // Helper function to traverse the sheet object and find the BPM
  const getBpm = (sheet) => {
    // Standard extraction pattern based on the console logs
    try {
      // Check for direct BPM property
      if (sheet && typeof sheet.bpm !== 'undefined') {
        return sheet.bpm;
      }
      
      // MongoDB might store the actual document in _doc
      if (sheet && sheet._doc && typeof sheet._doc.bpm !== 'undefined') {
        return sheet._doc.bpm;
      }
      
      // Check for BPM in song property
      if (sheet && sheet.song && typeof sheet.song.bpm !== 'undefined') {
        return sheet.song.bpm;
      }
      
      // Some sheets might store BPM in songInfo
      if (sheet && sheet.songInfo && typeof sheet.songInfo.bpm !== 'undefined') {
        return sheet.songInfo.bpm;
      }
      
      // Check for BPM in sections
      if (sheet && Array.isArray(sheet.sections) && sheet.sections.length > 0) {
        // Try each section for a BPM property
        for (const section of sheet.sections) {
          if (section && typeof section.bpm !== 'undefined') {
            return section.bpm;
          }
        }
      }
    } catch (error) {
      console.error("Error extracting BPM:", error);
    }
    
    // If we can't find a BPM, return a dash
    return "-";
  };

  // Local SheetCard component removed - using imported unified SheetCard component instead



  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Sheets</h1>
        
        {/* Local action buttons */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={handleCreateSheet}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
          >
            <PlusIcon className="w-3 h-3" />
            New Sheet
          </button>
          <button
            onClick={() => setImportModalOpen(true)}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
          >
            <Import className="w-3 h-3" />
            Import
          </button>
          <button
            onClick={() => setExportModalOpen(true)}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
          >
            <FileOutput className="w-3 h-3" />
            Export
          </button>
          {quickDraftEligible && (
            <button
              onClick={() => setQuickDraftModalOpen(true)}
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
            >
              <Wand2 className="w-3 h-3" />
              Quick Draft
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Sorting controls */}
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="title">Title</option>
              <option value="dateCreated">Date Added</option>
              <option value="dateModified">Date Modified</option>
              <option value="status">Status</option>
              <option value="owner">Owner</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2"
            >
              {sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            </Button>
          </div>
          
          {/* Search input */}
          <div className="relative w-full max-w-sm ml-auto flex items-center">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search sheets..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>
      
      {isLoading ? (
        <div className="text-center py-20">
          <p className="text-muted-foreground">Loading sheets...</p>
        </div>
      ) : (
        <div className="w-full">
          {/* Filter Tags */}
          <div className="mb-4 flex flex-wrap gap-2">
            {/* All filter */}
            <button
              onClick={() => toggleFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-full flex items-center gap-1.5 transition-colors ${
                activeFilters.has('all')
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All
            </button>

            {/* My Sheets filter */}
            {user && (
              <button
                onClick={() => toggleFilter('my_sheets')}
                className={`px-3 py-1.5 text-sm rounded-full flex items-center gap-1.5 transition-colors ${
                  activeFilters.has('my_sheets')
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                My Sheets
                {activeFilters.has('my_sheets') && (
                  <X className="h-3 w-3 ml-1" onClick={(e) => { e.stopPropagation(); toggleFilter('my_sheets'); }} />
                )}
              </button>
            )}

            {/* Band filters */}
            {availableBands.map(band => (
              <button
                key={band._id}
                onClick={() => toggleFilter(`band_${band._id}`)}
                className={`px-3 py-1.5 text-sm rounded-full flex items-center gap-1.5 transition-colors ${
                  activeFilters.has(`band_${band._id}`)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {band.name}
                {activeFilters.has(`band_${band._id}`) && (
                  <X className="h-3 w-3 ml-1" onClick={(e) => { e.stopPropagation(); toggleFilter(`band_${band._id}`); }} />
                )}
              </button>
            ))}
          </div>

          {/* Sheets Grid */}
          {(() => {
            const filteredSheets = getFilteredAndSortedSheets();
            const canManageSheet = (sheet) => user && (sheet.owner?._id === user._id || sheet.owner === user._id);

            return filteredSheets.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
                <p className="text-muted-foreground">
                  {allSheets.length === 0
                    ? "You don't have any sheets yet"
                    : "No sheets match the selected filters"}
                </p>
                {allSheets.length === 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={handleCreateSheet}
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create Sheet
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredSheets.map(sheet => (
                  <SheetCard
                    key={sheet._id}
                    sheet={sheet}
                    canManage={canManageSheet(sheet)}
                    context="sheets"
                    onRename={() => handleRename(sheet)}
                    onDelete={() => confirmDeleteSheet(sheet._id)}
                    onFork={() => handleFork(sheet)}
                    onExport={() => handleExportSheet(sheet)}
                    onAddToSetlist={() => handleAddToSetlist(sheet)}
                  />
                ))}
              </div>
            );
          })()}
        </div>
      )}
      
      {/* Confirmation Dialog */}
      <ConfirmModal
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="red"
      />
      
      {/* Setlist Modal */}
      {setlistModalOpen && (
        <SetlistModal
          isOpen={setlistModalOpen}
          onClose={() => setSetlistModalOpen(false)}
          sheet={selectedSheetForSetlist}
          onAddToSetlist={addSheetToSetlist}
        />
      )}
      
      {/* Import Modal */}
      <ImportModal 
        isOpen={importModalOpen} 
        onClose={() => setImportModalOpen(false)}
        onSuccess={() => {
          setImportModalOpen(false);
          // Refresh the sheets list after successful import
          getAllSheets().then(sheets => {
            if (sheets) {
              const processedSheets = sheets.map(sheet => ({
                ...sheet,
                bpm: sheet.bpm || (sheet.song?.bpm) || (sheet.songInfo?.bpm) || (sheet.sections?.[0]?.bpm) || null,
                title: sheet.title || (sheet.song?.title) || 'Untitled',
                artist: sheet.artist || (sheet.song?.artist) || 'Unknown Artist'
              }));
              setAllSheets(processedSheets);
            }
          });
        }}
      />
      
      {/* Export Modal */}
      <ExportModal 
        isOpen={exportModalOpen} 
        onClose={() => setExportModalOpen(false)}
      />
      
      {/* Quick Draft Modal */}
      <QuickDraftModal
        isOpen={quickDraftModalOpen}
        onClose={() => setQuickDraftModalOpen(false)}
        onSuccess={(sheet) => {
          setQuickDraftModalOpen(false);
          navigate(`/sheet/${sheet._id}`);
        }}
      />

      {/* Fork Options Modal */}
      <ForkOptionsModal
        isOpen={forkModalOpen}
        onClose={() => {
          setForkModalOpen(false);
          setSelectedSheetForFork(null);
        }}
        onConfirm={handleForkConfirm}
        sheetTitle={selectedSheetForFork?.title || ''}
        isOwnSheet={selectedSheetForFork?.owner === isAuthenticated?.user?._id}
      />
    </div>
  );
};

export default SheetsPage;
